/**
 * Scenario store — 12 canonical v2 scenarios + user-saved scenarios.
 *
 * CANONICAL_SCENARIOS_V2: readonly list of the 12 spec-defined scenarios.
 * useScenarioStore(): React hook backed by localStorage for SavedScenario[].
 */
import { useSyncExternalStore } from 'react';
import type { ScenarioAction, SavedScenario } from './simulator.js';

// ─── 12 canonical scenarios (spec §3) ────────────────────────────────────────

export const CANONICAL_SCENARIOS_V2: readonly ScenarioAction[] = [
  {
    id: 'goal-created',
    label: 'Goal Created',
    description: 'User sets a savings goal — traces Goal → GoalSummary → AI insight write.',
    writes: ['Goal'],
    fires: ['goal_created'],
    autonomyRequired: 0,
    isCanonical: true,
  },
  {
    id: 'budget-exceeded-alert',
    label: 'Budget Exceeded Alert',
    description: 'Spending event crosses budget threshold — alerts + budgetStatus update cascade.',
    writes: ['Transaction', 'BudgetCategory'],
    fires: ['transaction_created', 'budget_threshold_crossed'],
    autonomyRequired: 0,
    isCanonical: true,
  },
  {
    id: 'transaction-categorized',
    label: 'Transaction Categorized',
    description: 'AI CF auto-categorizes an imported transaction and fires a follow-up insight.',
    writes: ['Transaction'],
    fires: ['transaction_created'],
    autonomyRequired: 1,
    isCanonical: true,
  },
  {
    id: 'subscription-tier-upgraded',
    label: 'Subscription Tier Upgraded',
    description: 'Stripe payment success → tier validated → feature gates unlocked.',
    writes: ['StripeSubscription'],
    fires: ['subscription_updated'],
    autonomyRequired: 0,
    isCanonical: true,
    branchConditions: [
      {
        id: 'tier-validation-fail',
        label: 'Tier validation fails',
        reason: 'validation_error',
        probability: 0.05,
      },
    ],
  },
  {
    id: 'ai-suggestion-accepted',
    label: 'AI Suggestion Accepted',
    description: 'User accepts a copilot suggestion → agentic write cascade through budget + goal.',
    writes: ['AgentConfig'],
    fires: ['ai_suggestion_accepted'],
    autonomyRequired: 2,
    isCanonical: true,
  },
  {
    id: 'plaid-account-link-first-sync',
    label: 'Plaid Account Link → First Sync',
    description: 'OAuth flow completes → Account + PlaidConnection written → transaction import starts.',
    writes: ['Account', 'PlaidConnection'],
    fires: ['account_linked', 'plaid_sync_started'],
    autonomyRequired: 0,
    isCanonical: true,
    branchConditions: [
      {
        id: 'plaid-auth-fail',
        label: 'Plaid OAuth fails',
        reason: 'auth_fail',
        probability: 0.08,
      },
    ],
  },
  {
    id: 'stripe-webhook-tier-change',
    label: 'Stripe Webhook → Tier Change',
    description: 'Webhook arrives → tier validated → QuotaLimits recalculated → feature gates toggled.',
    writes: ['StripeSubscription', 'QuotaSettings'],
    fires: ['subscription_updated', 'tier_changed'],
    autonomyRequired: 0,
    isCanonical: true,
    branchConditions: [
      {
        id: 'stripe-signature-fail',
        label: 'HMAC signature invalid',
        reason: 'auth_fail',
        probability: 0.02,
      },
    ],
  },
  {
    id: 'budget-period-reset-scheduled',
    label: 'Budget Period Reset (Scheduled)',
    description: 'Monthly cron resets all BudgetCategory spent amounts and emits period_reset events.',
    writes: ['BudgetCategory', 'Budget'],
    fires: ['budget_period_reset'],
    autonomyRequired: 0,
    isCanonical: true,
  },
  {
    id: 'quota-exhaustion-retention-offer',
    label: 'Quota Exhaustion → Retention Offer',
    description: 'AI quota hits ceiling → retention offer generated → notification queued.',
    writes: ['UsageRecord'],
    fires: ['quota_exhausted', 'retention_offer_triggered'],
    autonomyRequired: 1,
    isCanonical: true,
    branchConditions: [
      {
        id: 'user-upgrades',
        label: 'User upgrades immediately',
        reason: 'custom',
        probability: 0.15,
      },
    ],
  },
  {
    id: 'ai-chat-mcp-tool-write',
    label: 'AI Chat → MCP Tool Write',
    description: 'Chat message triggers MCP tool → quota-checked write to Goal or Budget entity.',
    writes: ['AgentConfig'],
    fires: ['ai_message_sent', 'mcp_tool_executed'],
    autonomyRequired: 3,
    isCanonical: true,
    branchConditions: [
      {
        id: 'mcp-write-blocked',
        label: 'Constitutional gate blocks write',
        reason: 'cf_error',
        probability: 0.1,
      },
    ],
  },
  {
    id: 'wal-reconnect-drain',
    label: 'WAL Reconnect Drain',
    description: 'Network reconnect triggers IndexedDB WAL drain → queued mutations replayed in order.',
    writes: ['Transaction', 'Budget'],
    fires: ['offline_action_replayed'],
    autonomyRequired: 0,
    isCanonical: true,
    branchConditions: [
      {
        id: 'wal-conflict',
        label: 'Optimistic mutation conflict',
        reason: 'validation_error',
        probability: 0.12,
      },
    ],
  },
  {
    id: 'eventbus-circuit-breaker-trip',
    label: 'EventBus Circuit Breaker Trip',
    description: 'Event rate exceeds 100/s/type threshold → circuit breaker opens → events dropped.',
    writes: ['UsageRecord'],
    fires: ['circuit_breaker_state_changed', 'events_dropped_summary'],
    autonomyRequired: 0,
    isCanonical: true,
  },
];

// ─── Saved scenario store (localStorage-backed) ───────────────────────────────

const STORAGE_KEY = 'atlas_saved_scenarios_v2';

function loadFromStorage(): SavedScenario[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SavedScenario[]) : [];
  } catch {
    return [];
  }
}

function saveToStorage(scenarios: SavedScenario[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scenarios));
  } catch {
    // Storage quota exceeded — fail silently.
  }
}

type Listener = () => void;
const listeners = new Set<Listener>();
let _scenarios: SavedScenario[] = loadFromStorage();

function notify(): void {
  for (const l of listeners) l();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot(): SavedScenario[] {
  return _scenarios;
}

export const scenarioActions = {
  save(scenario: Omit<SavedScenario, 'id' | 'createdAt' | 'updatedAt'>): SavedScenario {
    const now = Date.now();
    const saved: SavedScenario = {
      ...scenario,
      id: `saved-${now}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt: now,
      updatedAt: now,
    };
    _scenarios = [..._scenarios, saved];
    saveToStorage(_scenarios);
    notify();
    return saved;
  },

  update(id: string, patch: Partial<Pick<SavedScenario, 'name' | 'action'>>): void {
    _scenarios = _scenarios.map((s) =>
      s.id === id ? { ...s, ...patch, updatedAt: Date.now() } : s,
    );
    saveToStorage(_scenarios);
    notify();
  },

  remove(id: string): void {
    _scenarios = _scenarios.filter((s) => s.id !== id);
    saveToStorage(_scenarios);
    notify();
  },

  clear(): void {
    _scenarios = [];
    saveToStorage(_scenarios);
    notify();
  },
};

/** React hook — returns the current list of user-saved scenarios. */
export function useScenarioStore(): SavedScenario[] {
  return useSyncExternalStore(subscribe, getSnapshot);
}
